import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button, StatusDot } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { AgentStatusMaterial } from "@/components/cursors/agent-status-surface";
import { useEdgeAwareBubblePlacement } from "@/components/cursors/use-edge-aware-bubble-placement";
import type { Rect } from "@/lib/edge-aware-placement";
import { cn } from "@/lib/cn";
import { useRevealStage } from "@/hooks/use-reveal-stage";
import {
  CONFIRM_MS,
  CROSSFADE_IN_MS,
  CROSSFADE_OUT_MS,
  MORPH_MS,
  prefersReducedMotion,
  REVEAL_DOT_SIZE,
  RETRACT_HEIGHT_DELAY_MS,
  RETRACT_HEIGHT_MS,
  RETRACT_WIDTH_MS,
  REVEAL_MS,
  RETRACT_MS,
  type RevealStage,
} from "@/lib/reveal-motion";
import {
  compactPillContentKey,
  type PillContent,
} from "./compact-cursor-pill-content";
import { PillText } from "./compact-cursor-pill-text";
import { CompactCursorPillQuestion } from "./compact-cursor-pill-question";
import { CompactCursorPillQuestionMeasure } from "./compact-cursor-pill-question-measure";
import { useCompactCursorPillDrag } from "./use-compact-cursor-pill-drag";
import {
  compactPillReserve,
  PILL_MAX_WIDTH,
  useCompactPillMeasure,
  PILL_TEXT_LEFT,
} from "./use-compact-pill-measure";
import { useT } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";

const PILL_TEXT_CLASS =
  "text-appkit-body font-normal leading-[1.4] [&_p]:leading-[1.4] [&_li]:leading-[1.4] [&_ul]:pl-3 [&_ol]:pl-4";

export function CompactCursorPill({
  content,
  visible = true,
  hovered = false,
  alwaysShowReply = false,
  canOpen = false,
  canDismiss = false,
  onOpen,
  onDismiss,
  onAnswerLater,
  skipOption,
  onAnswer,
  onAnswered,
  onExited,
  onRect,
  exclusions,
  questionError,
  actionContent,
  draggable = false,
  draggableKey,
}: {
  content: PillContent | null;
  visible?: boolean;
  hovered?: boolean;
  alwaysShowReply?: boolean;
  canOpen?: boolean;
  canDismiss?: boolean;
  onOpen?: () => void;
  onDismiss?: () => void;
  onAnswerLater?: () => void;
  /** When set, the question's X answers with this option instead of hiding it. */
  skipOption?: { id: string; label: string };
  onAnswer?: (text: string, optionId: string) => Promise<boolean>;
  onAnswered?: (awaitingInputId: string) => void;
  onExited?: () => void;
  onRect?: (rect: Rect) => void;
  exclusions?: Rect[];
  questionError?: string | null;
  /** Optional real-work actions inside the same measured production surface. */
  actionContent?: ReactNode;
  draggable?: boolean;
  draggableKey?: string;
}) {
  const t = useT(compactCursorMessages);
  const [displayed, setDisplayed] = useState<PillContent | null>(content);
  const [confirm, setConfirm] = useState<PillContent | null>(null);
  const reveal = useRevealStage(visible && (!!content || !!confirm), {
    onExited,
  });
  const restartReveal = reveal.restart;
  const [draft, setDraft] = useState("");
  const [fading, setFading] = useState(false);
  const [controlsFocused, setControlsFocused] = useState(false);
  const actionRailRef = useCallback((element: HTMLDivElement | null) => {
    // Removing a focused button does not dispatch blur.
    if (!element) setControlsFocused(false);
  }, []);
  const contentKey = compactPillContentKey(content);
  const priorKey = useRef(contentKey);
  // The follow loop re-renders every frame with an equal but new content
  // object. Keying the transition effect on the content key keeps those
  // renders from cancelling an in-flight crossfade timer.
  const latestContent = useRef(content);
  latestContent.current = content;
  const answerBusy = useRef(false);
  const active = confirm ?? displayed;
  const hasActions = !!actionContent && active?.kind !== "question" && !confirm;
  const replyInteractive = hovered && active?.kind === "reply";
  const reserve = compactPillReserve(active, canOpen, canDismiss);
  const measureKey = `${compactPillContentKey(active)}:${questionError ?? ""}:${hasActions}:${alwaysShowReply}`;
  const measure = useCompactPillMeasure(
    measureKey,
    reserve,
    active?.kind === "question" || hasActions ? 302 : undefined,
    active?.kind === "reply" && !hasActions,
  );
  const canExpand = active?.kind === "reply" && active.terminal && canOpen;
  const showsDismiss =
    active?.kind === "question" ||
    ((active?.kind === "reply" || active?.kind === "error") && canDismiss);
  // A settled answer or a question can be moved aside. The moved question
  // holds its place through its confirm and the next question step.
  const dragKind =
    confirm || active?.kind === "question"
      ? "question"
      : active?.kind === "reply" && active.terminal
        ? "reply"
        : null;
  const dragEnabled =
    draggable && !!dragKind && visible && reveal.stage === "idle";
  const positionerRef = useRef<HTMLDivElement>(null);
  const drag = useCompactCursorPillDrag({
    enabled: dragEnabled,
    resetKey: `${draggableKey}:${dragKind}`,
    containerRef: positionerRef,
    onRect,
  });
  const interactive =
    visible &&
    reveal.stage !== "exit" &&
    (canExpand ||
      showsDismiss ||
      replyInteractive ||
      hasActions ||
      dragEnabled);
  // Only a pill the user can act on holds still under the pointer. Status
  // pills trail the pointer, so it routinely overtakes them; freezing then
  // would pin the pill until the pointer left its rect.
  const placement = useEdgeAwareBubblePlacement({
    exclusions,
    reply: active?.kind === "reply",
    frozen: hovered && interactive,
    onRect,
    containerRef: positionerRef,
    manualPosition: drag.positionRef,
    positionResetKey: draggableKey,
  });
  useEffect(() => {
    const next = contentKey;
    const content = latestContent.current;
    if (next === priorKey.current) return;
    const previousKind = priorKey.current.split(":", 1)[0];
    const wasQuestion = displayed?.kind === "question";
    const isQuestion = content?.kind === "question";
    priorKey.current = next;
    if (!content) return;
    if (content.kind === "tool") {
      setDisplayed(content);
      setFading(false);
      return;
    }
    if (content.kind === "reply" && previousKind === "reply") {
      // Cursor replies arrive as frequent cumulative chunks. Replacing each
      // chunk through the fade timer cancels that timer on the next chunk,
      // leaving the bubble transparent for the whole stream.
      setDisplayed(content);
      setFading(false);
      return;
    }
    if (
      content.kind === "reply" &&
      (previousKind === "thinking" ||
        previousKind === "tool" ||
        previousKind === "empty")
    ) {
      // The first reply is often much shorter than the running status. Swap it
      // immediately so the shell begins shrinking with visible text instead
      // of showing an empty status-sized shell during the fade-out.
      setDisplayed(content);
      setFading(false);
      return;
    }
    if (isQuestion && !wasQuestion && displayed) {
      restartReveal();
      const timer = setTimeout(() => setDisplayed(content), 240);
      return () => clearTimeout(timer);
    }
    if (content.kind === "confirm") {
      setDisplayed(content);
      return;
    }
    setFading(true);
    const timer = setTimeout(() => {
      setDisplayed(content);
      setFading(false);
    }, CROSSFADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [contentKey, displayed, restartReveal]);

  const questionId =
    active?.kind === "question"
      ? active.item.question?.awaitingInputId
      : undefined;
  const answer = async (text: string, optionId: string) => {
    if (!onAnswer || !questionId || !text.trim() || answerBusy.current) return;
    answerBusy.current = true;
    setConfirm({
      kind: "confirm",
      label: optionId === "other" ? t("answerSent") : text,
    });
    const ok = await onAnswer(text.trim(), optionId).catch(() => false);
    answerBusy.current = false;
    if (!ok) {
      setConfirm(null);
      return;
    }
    setDraft("");
    setTimeout(() => {
      setConfirm(null);
      onAnswered?.(questionId);
    }, CONFIRM_MS);
  };

  const box = measure.box;
  // Native collection tracks width/height transitions. Keep the plate through
  // content updates so resizing never swaps between native and CSS material.
  const nativeSuspended = reveal.stage !== "idle";
  if (!reveal.mounted || !active) return null;
  const clipping = reveal.stage === "dot" || reveal.stage === "exit";
  const reducedMotion = prefersReducedMotion();
  const controlsVisible =
    (hovered || controlsFocused) && reveal.stage === "idle";
  const textClass = cn(
    PILL_TEXT_CLASS,
    active.kind === "reply" ? "[text-wrap:wrap]" : "[text-wrap:balance]",
  );
  const needsScroll = box.contentTop + box.contentHeight > box.height - 2;

  return (
    <div
      ref={placement.ref}
      className="relative w-max will-change-transform"
      data-testid="compact-cursor-pill-positioner"
      data-draggable={dragEnabled ? "true" : "false"}
    >
      <AgentStatusMaterial
        data-testid="compact-cursor-pill"
        data-native-glass-suspended={nativeSuspended ? "true" : undefined}
        data-agent-status-kind={active.kind}
        data-interactive={interactive ? "true" : undefined}
        {...drag.dragHandlers}
        className={cn(
          "relative overflow-hidden",
          dragEnabled &&
            (drag.dragging ? "cursor-grabbing select-none" : "cursor-grab"),
        )}
        style={{
          // CSS scales the radius to fit the shrinking box. Switching to the
          // dot's radius first would square the still-expanded closing bubble.
          borderRadius: box.radius,
          width: clipping ? REVEAL_DOT_SIZE : box.width,
          height: clipping ? REVEAL_DOT_SIZE : box.height,
          maxHeight:
            active.kind === "question" || hasActions
              ? "calc(100vh - 16px)"
              : 220,
          opacity: reveal.stage === "exit" ? 0 : 1,
          transition: reducedMotion
            ? "none"
            : `width ${reveal.stage === "exit" ? RETRACT_WIDTH_MS : MORPH_MS}ms ${reveal.stage === "exit" ? "ease-in" : "ease-out"}, height ${reveal.stage === "exit" ? RETRACT_HEIGHT_MS : MORPH_MS}ms ease-out ${reveal.stage === "exit" ? RETRACT_HEIGHT_DELAY_MS : 0}ms, opacity ${reveal.stage === "exit" ? RETRACT_MS - RETRACT_WIDTH_MS : 60}ms linear ${reveal.stage === "exit" ? RETRACT_WIDTH_MS : 0}ms`,
          pointerEvents: interactive ? "auto" : "none",
        }}
      >
        <div
          data-testid="compact-cursor-pill-scroll"
          className={cn(
            "absolute inset-0",
            needsScroll ? "overflow-y-auto" : "overflow-hidden",
          )}
          style={{
            clipPath: clipping
              ? `inset(${Math.max(0, (box.height - 6) / 2)}px 0 round ${box.radius}px)`
              : `inset(0 round ${box.radius}px)`,
            transition: reducedMotion
              ? "none"
              : `clip-path ${reveal.stage === "exit" ? RETRACT_HEIGHT_MS : REVEAL_MS}ms ${reveal.stage === "exit" ? "ease-in" : "ease-out"} ${reveal.stage === "exit" ? RETRACT_HEIGHT_DELAY_MS : 0}ms`,
          }}
        >
          <div
            ref={measure.liveRef}
            data-testid="compact-cursor-pill-content"
            className={cn("absolute left-3.5", textClass)}
            style={{
              top: box.contentTop,
              width: box.width - PILL_TEXT_LEFT - reserve,
              opacity: fading ? 0 : 1,
              transition: reducedMotion
                ? "none"
                : `opacity ${fading ? CROSSFADE_OUT_MS : CROSSFADE_IN_MS}ms ${fading ? "linear" : "ease-out"}`,
            }}
          >
            {active.kind === "question" && !confirm ? (
              <div>
                <span className="flex items-start gap-1.5">
                  <span className="flex h-[1lh] shrink-0 items-center">
                    <StatusDot tone="question" />
                  </span>
                  <CompactCursorPillQuestion
                    item={active.item}
                    hovered={hovered}
                    alwaysShowReply={alwaysShowReply}
                    disabled={answerBusy.current}
                    draft={draft}
                    onDraftChange={setDraft}
                    onAnswer={(text, optionId) => void answer(text, optionId)}
                  />
                </span>
                {questionError && (
                  <p
                    role="alert"
                    className="mt-2 text-appkit-caption text-danger-light"
                  >
                    {questionError}
                  </p>
                )}
              </div>
            ) : (
              <PillText content={active} />
            )}
            {hasActions && actionContent}
          </div>
        </div>
        {(canOpen || showsDismiss) && (
          <div
            ref={actionRailRef}
            data-testid="compact-cursor-pill-actions"
            className="absolute right-1 z-10 flex gap-0.5 transition-opacity duration-[120ms] motion-reduce:transition-none"
            style={{ top: box.contentTop, opacity: controlsVisible ? 1 : 0 }}
            inert={!interactive || reveal.stage !== "idle"}
            onFocusCapture={() => setControlsFocused(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setControlsFocused(false);
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {canExpand && (
              <Button
                type="button"
                variant="ghost"
                size="iconXs"
                aria-label={t("openConversation")}
                data-testid="compact-cursor-pill-open"
                onClick={onOpen}
                className="text-muted hover:text-primary"
              >
                <Icons.Maximize className="h-3 w-3" />
              </Button>
            )}
            {showsDismiss && (
              <Button
                type="button"
                variant="ghost"
                size="iconXs"
                aria-label={
                  active.kind !== "question"
                    ? t("dismissCursor")
                    : (skipOption?.label ?? t("answerLater"))
                }
                title={
                  active.kind === "question" ? skipOption?.label : undefined
                }
                data-testid="compact-cursor-pill-dismiss"
                onClick={
                  active.kind !== "question"
                    ? onDismiss
                    : skipOption
                      ? () => void answer(skipOption.label, skipOption.id)
                      : onAnswerLater
                }
                className="text-muted hover:text-primary"
              >
                <Icons.X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </AgentStatusMaterial>
      <div
        ref={measure.cloneRef}
        aria-hidden="true"
        inert
        data-testid="compact-cursor-pill-measure"
        className={cn(
          "pointer-events-none invisible absolute left-0 top-0 w-max",
          textClass,
        )}
        style={{ maxWidth: PILL_MAX_WIDTH - PILL_TEXT_LEFT - reserve }}
      >
        {active.kind === "question" ? (
          active.item.question && (
            <CompactCursorPillQuestionMeasure
              question={active.item.question}
              showReply={alwaysShowReply || hovered || !!draft}
              draft={draft}
            />
          )
        ) : (
          <PillText content={active} />
        )}
        {hasActions && actionContent}
        {active.kind === "question" && questionError && (
          <p className="mt-2 text-appkit-caption">{questionError}</p>
        )}
      </div>
    </div>
  );
}
export type { PillContent, RevealStage };
